// Activation — collects payment details and turns an approved
// consultation into an active subscription.
//
// Shown inline on the Plan tab's APPROVED section, so this component
// renders FORM-ONLY (no hero / no page chrome) — the section above
// supplies the editorial framing.
//
// Visual contract:
//   • Header: peach band labelled "CONFIRM CARD" (we don't store cards,
//     so every activation collects a fresh card).
//   • Body:    Stripe split card elements (Number / Expiry / CVC / ZIP)
//     for a clean mobile layout — no jumbled single-line CardElement.
//   • Footer:  "Have a coupon?" disclosure → validates against the
//     backend, applies a $ or % discount and updates the activation
//     summary + Stripe charge.
//   • CTA:     Black (variant="primary") activate button — "ACTIVATE —
//     $XXX/MO". Disabled until card is complete.
import { useEffect, useMemo, useState } from "react";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Button from "@/components/Button";
import { Spinner } from "@/components/Loader";
import { apiPost, apiPut } from "@/lib/api";
import { getStripe } from "@/lib/stripe";
import { useAuth } from "@/lib/auth";
import { fmtMoney } from "@/lib/format";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";

export default function Activation({ consultation, onActivated }) {
  const [stripePromise, setStripePromise] = useState(null);
  useEffect(() => {
    getStripe().then(setStripePromise).catch(() => setStripePromise(null));
  }, []);
  if (!stripePromise) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spinner />
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <ActivationForm consultation={consultation} onActivated={onActivated} />
    </Elements>
  );
}

function ActivationForm({ consultation, onActivated }) {
  const stripe = useStripe();
  const elements = useElements();
  const { consumer, refreshProfile } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [postalCode, setPostalCode] = useState("");
  const [completed, setCompleted] = useState({ number: false, expiry: false, cvc: false });
  const [error, setError] = useState(null);

  // Shipping address state. When the member already has an address on
  // their profile we display a "SHIPPING TO" summary + Change link;
  // otherwise we expand the form inline so they can fill it in before
  // activating. Either way the address is saved back to /consumer/profile
  // before /activate-subscription fires so the BE can ship from a single
  // source of truth.
  const savedAddress = consumer?.shippingAddress || null;
  const hasSavedAddress = !!(savedAddress && savedAddress.line1 && savedAddress.city);
  const [editingAddress, setEditingAddress] = useState(!hasSavedAddress);
  const [address, setAddress] = useState({
    line1: savedAddress?.line1 || "",
    line2: savedAddress?.line2 || "",
    city: savedAddress?.city || "",
    state: savedAddress?.state || "",
    zip: savedAddress?.zip || "",
  });
  // Keep local state in sync if the consumer profile reloads (e.g.,
  // after a save or coming back to the page).
  useEffect(() => {
    if (savedAddress) {
      setAddress({
        line1: savedAddress.line1 || "",
        line2: savedAddress.line2 || "",
        city: savedAddress.city || "",
        state: savedAddress.state || "",
        zip: savedAddress.zip || "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddress?.line1, savedAddress?.city, savedAddress?.state, savedAddress?.zip]);

  // Coupon disclosure state. Coupon validates against the backend the
  // moment the user submits the code (Apply button) — we never trust
  // a client-applied discount; the activate-subscription endpoint
  // re-validates and is the source of truth for the final charge.
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const [coupon, setCoupon] = useState(null); // { code, discountType, discountValue, finalAmount, message }

  // Brand-matched element styling. Each split element shares the same
  // typography so the card row reads as a single object.
  const elementOptions = useMemo(() => ({
    style: {
      base: {
        fontFamily: '"DM Sans", -apple-system, BlinkMacSystemFont, sans-serif',
        fontSize: "16px", // 16px+ keeps iOS Safari from auto-zooming on focus
        color: "#0F0E0D",
        "::placeholder": { color: "#A8A29E" },
      },
      invalid: { color: "#C04E3A" },
    },
  }), []);

  const monthly = Number(consultation.monthlyPrice || 0);
  // Final charge — backend remains source of truth, but surface the
  // expected amount on the button so members see the discount apply
  // immediately.
  const charge = coupon?.finalAmount != null ? Number(coupon.finalAmount) : monthly;
  const discount = monthly - charge;

  const cardComplete = completed.number && completed.expiry && completed.cvc && /^\d{3,10}$/.test(postalCode.trim());
  // Address considered ready when either we have one already saved
  // (and the member hasn't opened the editor) OR the inline form is
  // populated with line1/city/state/zip.
  const addressReady = hasSavedAddress && !editingAddress
    ? true
    : !!(address.line1.trim() && address.city.trim() && address.state.trim() && /^\d{5}(-\d{4})?$/.test(address.zip.trim()));
  const ready = cardComplete && addressReady;

  const submitCoupon = async () => {
    const code = couponInput.trim();
    if (!code || couponBusy) return;
    setCouponBusy(true);
    setError(null);
    try {
      const res = await apiPost("/consumer/coupons/validate", {
        code,
        consultationId: consultation._id || consultation.id,
        purpose: "subscription",
        baseAmount: monthly,
      });
      if (!res?.valid) {
        toast(res?.message || "That code isn't valid.", "error");
        setCoupon(null);
        return;
      }
      setCoupon({
        code: res.code || code,
        discountType: res.discountType || "amount",
        discountValue: res.discountValue,
        finalAmount: res.finalAmount,
        message: res.message,
      });
      toast("Coupon applied.", "success");
    } catch (err) {
      // 404 → endpoint not deployed on this environment yet. Surface a
      // friendly message that tells the member what's actually going on
      // rather than the raw fetch error.
      // Anything else → use the API's detail message.
      let msg;
      if (err?.status === 404) {
        msg = "Coupons aren't enabled yet — check back soon.";
      } else {
        msg = err?.detail || "That code isn't valid.";
      }
      toast(msg, "error");
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !stripe || !elements) return;
    setError(null);
    if (!cardComplete) {
      setError("Please complete your card details.");
      return;
    }
    if (!addressReady) {
      setError("Please add a shipping address before activating.");
      return;
    }
    setSubmitting(true);
    try {
      // Persist address first if the member edited it (or didn't have
      // one stored). Backend is the source of truth — we save before
      // activating so the subscription document gets the right address
      // straight from the profile.
      if (editingAddress || !hasSavedAddress) {
        await apiPut("/consumer/profile", {
          shippingAddress: {
            line1: address.line1.trim(),
            line2: address.line2.trim(),
            city: address.city.trim(),
            state: address.state.trim().toUpperCase(),
            zip: address.zip.trim(),
          },
        });
        await refreshProfile?.();
      }
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: { address: { postal_code: postalCode.trim() } },
      });
      if (result.error) throw new Error(result.error.message);
      const paymentMethodId = result.paymentMethod.id;

      const res = await apiPost(
        `/consumer/consultations/${consultation._id || consultation.id}/activate-subscription`,
        {
          paymentMethodId,
          // Pass the validated coupon code so backend can re-apply it
          // server-side. Backend is the source of truth for the final
          // amount — frontend just hints what we expect.
          couponCode: coupon?.code || undefined,
        },
      );
      // SCA / 3DS handoff — Stripe returns a clientSecret when the issuer
      // requires extra authentication.
      if (res?.stripe?.status === "incomplete" && res.stripe.clientSecret) {
        const conf = await stripe.confirmCardPayment(res.stripe.clientSecret);
        if (conf.error) throw new Error(conf.error.message);
      }
      toast("Subscription activated.", "success");
      onActivated && (await onActivated());
    } catch (err) {
      setError(err.detail || err.message || "Activation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // Discount summary line in the card header. Renders only when a
  // coupon is applied so the layout stays calm pre-coupon.
  const discountLine = coupon
    ? coupon.discountType === "percent"
      ? `${coupon.discountValue}% off`
      : `${fmtMoney(discount)} off`
    : null;

  return (
    <form onSubmit={submit} data-testid="plan-activation-form">
      {/* PROGRAM SUMMARY — what the member is paying for, surfaced
          above the card form so they never wonder. Pulls program name +
          tier + monthly price from the consultation. */}
      <ProgramSummary consultation={consultation} monthly={monthly} />

      {/* CONFIRM CARD header — peach band. Replaces both the old "PAYMENT
          METHOD" eyebrow and the redundant existing-card option (we
          don't store cards). */}
      <div
        style={{
          background: C.peach,
          color: C.peachDark,
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDark }}>
          ★ CONFIRM CARD
        </span>
        <span style={{ fontSize: 12, color: C.peachDark, opacity: 0.85 }}>
          {discountLine ? <>{discountLine} · charged today: <strong style={{ color: C.ink }}>{fmtMoney(charge)}</strong></> : <>charged today: <strong style={{ color: C.ink }}>{fmtMoney(charge)}/mo</strong></>}
        </span>
      </div>

      {/* Card fields — split elements give us a clean mobile-friendly
          grid: full-width number row, then a 2-up expiry/cvc row, then
          full-width postal code. */}
      <div style={{ background: C.pure, border: `1.5px solid ${C.warmGray3}`, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
        <Field label="CARD NUMBER" testId="card-number-wrap">
          <CardNumberElement
            options={{ ...elementOptions, showIcon: true, placeholder: "1234 1234 1234 1234" }}
            onChange={(ev) => setCompleted((c) => ({ ...c, number: ev.complete }))}
          />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="EXPIRY" testId="card-expiry-wrap">
            <CardExpiryElement
              options={elementOptions}
              onChange={(ev) => setCompleted((c) => ({ ...c, expiry: ev.complete }))}
            />
          </Field>
          <Field label="CVC" testId="card-cvc-wrap">
            <CardCvcElement
              options={elementOptions}
              onChange={(ev) => setCompleted((c) => ({ ...c, cvc: ev.complete }))}
            />
          </Field>
        </div>
        <Field label="ZIP / POSTAL CODE" testId="card-postal-wrap">
          <input
            inputMode="numeric"
            autoComplete="postal-code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            placeholder="90210"
            data-testid="card-postal-input"
            style={{
              width: "100%",
              border: 0,
              outline: "none",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 16, // 16+ to prevent iOS auto-zoom
              color: C.ink,
              padding: 0,
              background: "transparent",
            }}
          />
        </Field>
      </div>

      {/* SHIPPING ADDRESS — under the card form. If the member already
          has one on file we show a confirm-only summary with a Change
          link. If not, we expand the form inline so they can fill it
          in before activating. The address is saved to the profile on
          submit so backend has a single source of truth. */}
      <AddressBlock
        savedAddress={savedAddress}
        hasSavedAddress={hasSavedAddress}
        editing={editingAddress}
        onEdit={() => setEditingAddress(true)}
        onCancelEdit={hasSavedAddress ? () => {
          // Revert to saved values when the member backs out of editing.
          setAddress({
            line1: savedAddress?.line1 || "",
            line2: savedAddress?.line2 || "",
            city: savedAddress?.city || "",
            state: savedAddress?.state || "",
            zip: savedAddress?.zip || "",
          });
          setEditingAddress(false);
        } : null}
        address={address}
        setAddress={setAddress}
      />

      {/* Coupon disclosure — minimal until tapped, then expands inline. */}
      <div style={{ marginTop: 16 }}>
        {!couponOpen && !coupon && (
          <button
            type="button"
            data-testid="coupon-open-link"
            onClick={() => setCouponOpen(true)}
            style={{
              background: "transparent",
              border: 0,
              padding: 0,
              fontSize: 13,
              fontWeight: 700,
              color: C.ink,
              textDecoration: "underline",
              cursor: "pointer",
              letterSpacing: "0.02em",
            }}
          >
            Have a coupon?
          </button>
        )}
        {couponOpen && !coupon && (
          <div
            data-testid="coupon-input-row"
            style={{
              display: "flex",
              gap: 8,
              alignItems: "stretch",
              padding: "10px 12px",
              background: C.bone,
              border: `1px solid ${C.warmGray3}`,
            }}
          >
            <input
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              data-testid="coupon-input"
              autoCapitalize="characters"
              style={{
                flex: 1,
                border: 0,
                outline: "none",
                background: "transparent",
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: C.ink,
                padding: "6px 4px",
              }}
            />
            <button
              type="button"
              data-testid="coupon-apply"
              onClick={submitCoupon}
              disabled={couponBusy || !couponInput.trim()}
              style={{
                padding: "8px 14px",
                background: C.ink,
                color: C.cream,
                border: 0,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                cursor: couponBusy ? "wait" : "pointer",
                opacity: couponBusy || !couponInput.trim() ? 0.6 : 1,
              }}
            >
              {couponBusy ? "…" : "APPLY"}
            </button>
          </div>
        )}
        {coupon && (
          <div
            data-testid="coupon-applied"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 14px",
              background: C.sage,
              color: C.sageDark,
              gap: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.18em" }}>
                ✓ {coupon.code} APPLIED
              </div>
              <div style={{ marginTop: 2, fontSize: 12, opacity: 0.85 }}>
                {discountLine}{coupon.message ? ` · ${coupon.message}` : ""}
              </div>
            </div>
            <button
              type="button"
              data-testid="coupon-remove"
              onClick={removeCoupon}
              aria-label="Remove coupon"
              style={{ background: "transparent", border: 0, color: C.sageDark, fontSize: 18, cursor: "pointer", padding: 4, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        )}
      </div>

      {error && (
        <p data-testid="activation-error" style={{ marginTop: 14, fontSize: 13, color: C.coral, lineHeight: 1.5 }}>
          {error}
        </p>
      )}

      <div style={{ marginTop: 22 }}>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          testId="activate-btn"
          disabled={submitting || !ready}
        >
          {submitting ? "ACTIVATING…" : `ACTIVATE — ${fmtMoney(charge)}/MO ★`}
        </Button>
        <div style={{ marginTop: 12, fontSize: 12, color: C.warmGray, textAlign: "center", lineHeight: 1.55 }}>
          First shipment in 48 hours · Cancel anytime · auto-renews monthly<br />
          Charges show as <strong>VOYA9.COM</strong> on your statement.
        </div>
      </div>
    </form>
  );
}

function Field({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ padding: "12px 12px", border: `1.5px solid ${C.warmGray3}`, background: C.pure }}>
        {children}
      </div>
    </div>
  );
}

// Shipping address block. Two states:
//   1. Confirm — member has a saved address, showing a one-line summary
//      with a [Change] link. Default when consumer.shippingAddress.line1
//      exists.
//   2. Edit — inline form with line 1/2, city, state, zip. Default when
//      no address exists, OR when the member taps Change.
function AddressBlock({ savedAddress, hasSavedAddress, editing, onEdit, onCancelEdit, address, setAddress }) {
  const updateField = (key) => (e) => {
    const v = e.target.value;
    setAddress((p) => ({
      ...p,
      [key]: key === "state" ? v.toUpperCase().slice(0, 2) : v,
    }));
  };

  if (!editing && hasSavedAddress) {
    // Confirm-only summary — calm, single line + Change pill
    const line2 = savedAddress.line2 ? `, ${savedAddress.line2}` : "";
    return (
      <div
        data-testid="activation-address-confirm"
        style={{
          marginTop: 16,
          padding: "16px 18px",
          background: C.bone,
          border: `1px solid ${C.warmGray3}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
            ★ SHIPPING TO
          </div>
          <div style={{ marginTop: 6, fontSize: 14, color: C.ink, lineHeight: 1.45 }}>
            {savedAddress.line1}{line2}
            <br />
            {savedAddress.city}, {savedAddress.state} {savedAddress.zip}
          </div>
        </div>
        <button
          type="button"
          data-testid="activation-address-change"
          onClick={onEdit}
          style={{
            background: "transparent",
            border: 0,
            padding: "4px 0",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.18em",
            color: C.ink,
            textDecoration: "underline",
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          CHANGE
        </button>
      </div>
    );
  }

  // Edit form
  return (
    <div
      data-testid="activation-address-edit"
      style={{
        marginTop: 16,
        background: C.pure,
        border: `1.5px solid ${C.warmGray3}`,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peachDeep }}>
          ★ SHIPPING ADDRESS
        </div>
        {onCancelEdit && (
          <button
            type="button"
            data-testid="activation-address-cancel"
            onClick={onCancelEdit}
            style={{
              background: "transparent",
              border: 0,
              padding: "4px 0",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              color: C.warmGray,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            CANCEL
          </button>
        )}
      </div>
      <Field label="ADDRESS LINE 1" testId="addr-line1-wrap">
        <input
          autoComplete="address-line1"
          value={address.line1}
          onChange={updateField("line1")}
          placeholder="123 Main St"
          data-testid="addr-line1-input"
          style={addressInputStyle}
        />
      </Field>
      <Field label="ADDRESS LINE 2 (OPTIONAL)" testId="addr-line2-wrap">
        <input
          autoComplete="address-line2"
          value={address.line2}
          onChange={updateField("line2")}
          placeholder="Apt 4B"
          data-testid="addr-line2-input"
          style={addressInputStyle}
        />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1.2fr", gap: 12 }}>
        <Field label="CITY" testId="addr-city-wrap">
          <input
            autoComplete="address-level2"
            value={address.city}
            onChange={updateField("city")}
            placeholder="Beverly Hills"
            data-testid="addr-city-input"
            style={addressInputStyle}
          />
        </Field>
        <Field label="STATE" testId="addr-state-wrap">
          <input
            autoComplete="address-level1"
            value={address.state}
            onChange={updateField("state")}
            placeholder="CA"
            maxLength={2}
            data-testid="addr-state-input"
            style={addressInputStyle}
          />
        </Field>
        <Field label="ZIP" testId="addr-zip-wrap">
          <input
            inputMode="numeric"
            autoComplete="postal-code"
            value={address.zip}
            onChange={updateField("zip")}
            placeholder="90210"
            maxLength={10}
            data-testid="addr-zip-input"
            style={addressInputStyle}
          />
        </Field>
      </div>
    </div>
  );
}

const addressInputStyle = {
  width: "100%",
  border: 0,
  outline: "none",
  fontFamily: '"DM Sans", sans-serif',
  fontSize: 16, // 16+ to prevent iOS auto-zoom
  color: C.ink,
  padding: 0,
  background: "transparent",
};

// What the member is paying for. Mirrors the editorial language used on
// the approved hero so the summary reads as a continuation of that
// moment, not a transactional checkout block.
function ProgramSummary({ consultation, monthly }) {
  const programName =
    consultation.programName ||
    consultation.selectedProductName ||
    consultation.productName ||
    "Your program";
  const tier =
    consultation.tierLabel ||
    consultation.selectedProtocolTier ||
    consultation.tierName ||
    "";
  return (
    <div
      data-testid="activation-program-summary"
      style={{
        background: C.ink,
        color: C.cream,
        padding: "20px 22px",
        marginBottom: 12,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.peach }}>
          ★ PROGRAM SUMMARY
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 22,
            fontWeight: 800,
            letterSpacing: "-0.01em",
            textTransform: "uppercase",
            lineHeight: 1.15,
          }}
        >
          {programName}
        </div>
        {tier && (
          <div style={{ marginTop: 4, fontSize: 13, color: "rgba(250,246,240,0.72)", letterSpacing: "0.04em" }}>
            {tier} tier
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <span style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", color: C.cream }}>
          {fmtMoney(monthly)}
        </span>
        <span style={{ fontSize: 13, color: "rgba(250,246,240,0.6)", fontWeight: 500 }}>/mo</span>
      </div>
    </div>
  );
}
